import { gql, NetworkStatus, type TypedDocumentNode } from '@apollo/client'
import { QueryRef, useMutation, useReadQuery } from '@apollo/client/react'
import { ErrorAlert } from '@repo/shared-client/components/blocks'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import SFBackPageHeader from '@web/src/components/primatives/sf-back-page-header/sf-back-page-header'
import SFPageLoading from '@web/src/components/primatives/sf-page-loading/sf-page-loading'
import RendezvousForm from '@web/src/features/rendezvous/rendezvous-form/rendezvous-form'
import { queryAndPreload } from '@web/src/lib/apollo/query-and-preload'
import { env } from '@web/src/lib/env.client'
import { useTranslation } from '@web/src/lib/intl/react'
import { robotsMetaForEnvironment, seo } from '@web/src/lib/seo'
import { useState } from 'react'
import toast from 'react-hot-toast'
import type {
  EditRendezvousQuery,
  EditRendezvousQueryVariables,
  UpdateRendezvousInput,
  UpdateRendezvousMutation,
  UpdateRendezvousMutationVariables,
} from 'types/graphql'

const EDIT_RENDEZVOUS_QUERY: TypedDocumentNode<EditRendezvousQuery, EditRendezvousQueryVariables> = gql`
  query EditRendezvousQuery($id: String!) {
    rendezvous(id: $id) {
      id
      rendezvousTime
      rendezvousType
      description
      location
      locationLat
      locationLong
    }
  }
`

const UPDATE_RENDEZVOUS_MUTATION: TypedDocumentNode<UpdateRendezvousMutation, UpdateRendezvousMutationVariables> = gql`
  mutation UpdateRendezvousMutation($id: String!, $input: UpdateRendezvousInput!) {
    updateRendezvous(id: $id, input: $input) {
      id
    }
  }
`

export const Route = createFileRoute('/(auth)/(main-layout)/rendezvous/$id/edit')({
  component: EditRendezvousPage,
  head: () =>
    seo({
      title: `Rendezvous Edit | ${env.VITE_APP_NAME}`,
      description: 'Edit your rendezvous event. Invite your friends to join you for a fun night out.',
      robots: robotsMetaForEnvironment({ all: 'noindex,nofollow' }),
    }),
  loader: async ({ context, params }) => {
    const { queryRef } = await queryAndPreload(context, EDIT_RENDEZVOUS_QUERY, {
      variables: { id: params.id },
    })
    return { queryRef }
  },
})

function EditRendezvousPage() {
  const { t } = useTranslation()
  const { id } = Route.useParams()
  const [showSpinner, setShowSpinnerValue] = useState(false)
  const navigate = useNavigate()
  const { queryRef } = Route.useLoaderData()
  const { data, error, networkStatus } = useReadQuery(queryRef as QueryRef<EditRendezvousQuery>)
  const isInitialLoading = networkStatus === NetworkStatus.loading

  const [updateRendezvous] = useMutation(UPDATE_RENDEZVOUS_MUTATION, {
    onCompleted: () => {
      toast.success(t('Rendezvous updated successfully!'))
      navigate({ to: '/rendezvous' })
      setShowSpinnerValue(false)
    },
    onError: (mutationError) => {
      toast.error(mutationError.message)
      setShowSpinnerValue(false)
    },
  })

  const onSave = (input: UpdateRendezvousInput) => {
    setShowSpinnerValue(true)
    updateRendezvous({
      variables: {
        id,
        input,
      },
    })
  }

  const handleCancel = () => {
    navigate({ to: '/rendezvous' })
  }

  if (isInitialLoading) {
    return <SFPageLoading />
  }

  if (error) {
    return <ErrorAlert error={error} />
  }

  if (!data?.rendezvous) {
    return <ErrorAlert error={new Error(t('Rendezvous not found'))} />
  }

  return (
    <div className="mx-auto w-full max-w-2xl px-4 pt-4">
      <div className="flex w-full flex-col items-stretch gap-4">
        <SFBackPageHeader
          lowerText={t('Update your rendezvous details')}
          onBackClick={handleCancel}
          title={t('Edit Rendezvous')}
        />
        <RendezvousForm onSave={onSave} rendezvous={data.rendezvous} showSpinner={showSpinner} />
      </div>
    </div>
  )
}
